import { useState } from 'react';
import { encounterAPI } from '../../lib/api';
import { 
  MagnifyingGlass,
  User,
  Calendar,
  Clock,
  VideoCamera,
  Stethoscope,
  CaretRight,
  X,
  NotePencil,
  Plus,
  CheckCircle,
  Warning,
  ClipboardText
} from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function EncounterSearchPage() {
  // Search state
  const [lastName, setLastName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [dob, setDob] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState(null);
  
  // Selected patient encounters
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [encounters, setEncounters] = useState([]);
  const [loadingEncounters, setLoadingEncounters] = useState(false);
  
  // Encounter detail/addendum
  const [selectedEncounter, setSelectedEncounter] = useState(null);
  const [addendumModalOpen, setAddendumModalOpen] = useState(false);
  const [addendumText, setAddendumText] = useState('');
  const [addendumReason, setAddendumReason] = useState('');
  const [savingAddendum, setSavingAddendum] = useState(false);

  const handleSearch = async () => {
    if (!lastName && !firstName && !dob) {
      toast.error('Please enter at least one search criteria');
      return;
    }

    setSearching(true);
    setSearchResults(null);
    setSelectedPatient(null);
    setEncounters([]);
    
    try {
      const response = await encounterAPI.searchPatients(lastName, firstName, dob);
      setSearchResults(response.data);
      
      if (response.data.patients.length === 0) {
        toast.info('No patients found matching your search criteria');
      }
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('Search error:', error);
      toast.error(error.response?.data?.detail || 'Search failed');
    } finally {
      setSearching(false);
    }
  };

  const handleSelectPatient = async (patient) => {
    setSelectedPatient(patient);
    setLoadingEncounters(true);
    
    try {
      const response = await encounterAPI.getPatientEncounters(patient.id);
      setEncounters(response.data.encounters);
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('Error loading encounters:', error);
      toast.error('Failed to load encounters');
    } finally {
      setLoadingEncounters(false);
    }
  };

  const handleAddAddendum = async () => {
    if (!addendumText.trim()) {
      toast.error('Please enter addendum text');
      return;
    }

    setSavingAddendum(true);
    try {
      const response = await encounterAPI.addAddendum(
        selectedEncounter.id,
        addendumText,
        addendumReason || null
      );
      
      toast.success('Addendum added successfully');
      
      // Update local state
      setEncounters(prev => prev.map(enc => 
        enc.id === selectedEncounter.id 
          ? { ...enc, addendums: [...(enc.addendums || []), response.data.addendum] }
          : enc
      ));
      
      // Update selected encounter
      setSelectedEncounter(prev => ({
        ...prev,
        addendums: [...(prev.addendums || []), response.data.addendum]
      }));
      
      setAddendumModalOpen(false);
      setAddendumText('');
      setAddendumReason('');
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('Addendum error:', error);
      toast.error(error.response?.data?.detail || 'Failed to add addendum');
    } finally {
      setSavingAddendum(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return <span className="status-badge status-completed">Completed</span>;
      case 'in_progress':
        return <span className="status-badge status-in-progress">In Progress</span>;
      case 'scheduled':
        return <span className="status-badge status-pending">Scheduled</span>;
      default:
        return <span className="status-badge bg-slate-100 text-slate-700">{status}</span>;
    }
  };

  const clearSearch = () => {
    setLastName('');
    setFirstName('');
    setDob('');
    setSearchResults(null);
    setSelectedPatient(null);
    setEncounters([]);
    setSelectedEncounter(null);
  };

  return (
    <div className="p-8" data-testid="encounter-search-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope' }}>
          Encounter Search
        </h1>
        <p className="text-slate-500 mt-1">
          Search for patients by name and DOB to view their encounter history
        </p>
      </div>

      {/* Search Form */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-6">
        <div className="grid md:grid-cols-4 gap-4">
          <div>
            <label className="label-clinical">Last Name</label>
            <input
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Smith"
              className="input-clinical"
              data-testid="search-last-name"
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
          </div>
          <div>
            <label className="label-clinical">First Name</label>
            <input
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="John"
              className="input-clinical"
              data-testid="search-first-name"
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
          </div>
          <div>
            <label className="label-clinical">Date of Birth</label>
            <input
              type="date"
              value={dob}
              onChange={(e) => setDob(e.target.value)}
              className="input-clinical"
              data-testid="search-dob"
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
          </div>
          <div className="flex items-end gap-2">
            <button
              onClick={handleSearch}
              disabled={searching}
              className="btn-teal flex items-center gap-2 flex-1"
              data-testid="search-btn"
            >
              {searching ? (
                <>
                  <div className="spinner-sm" />
                  Searching...
                </>
              ) : (
                <>
                  <MagnifyingGlass size={18} />
                  Search
                </>
              )}
            </button>
            {(lastName || firstName || dob) && (
              <button
                onClick={clearSearch}
                className="btn-outline p-3"
                title="Clear"
              >
                <X size={18} />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Results Layout */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Patient Results */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl shadow-sm border border-slate-200">
            <div className="p-4 border-b border-slate-200">
              <h2 className="font-semibold text-slate-900">
                Patients {searchResults && `(${searchResults.total})`}
              </h2>
            </div>
            <div className="divide-y divide-slate-100 max-h-[500px] overflow-y-auto">
              {searchResults?.patients.map((patient) => (
                <div
                  key={patient.id}
                  onClick={() => handleSelectPatient(patient)}
                  className={`p-4 cursor-pointer hover:bg-slate-50 transition-colors ${
                    selectedPatient?.id === patient.id ? 'bg-teal-50 border-l-4 border-teal-500' : ''
                  }`}
                  data-testid={`patient-result-${patient.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
                        <User size={20} className="text-slate-500" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">
                          {patient.last_name}, {patient.first_name}
                        </p>
                        <p className="text-sm text-slate-500">
                          DOB: {patient.date_of_birth || 'N/A'}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="text-xs font-medium text-teal-600 bg-teal-50 px-2 py-1 rounded-full">
                        {patient.encounter_count} visits
                      </span>
                      <CaretRight size={16} className="text-slate-400 ml-2 inline" />
                    </div>
                  </div>
                </div>
              ))}
              
              {searchResults?.patients.length === 0 && (
                <div className="p-8 text-center">
                  <User size={40} className="text-slate-300 mx-auto mb-2" />
                  <p className="text-slate-500">No patients found</p>
                </div>
              )}
              
              {!searchResults && (
                <div className="p-8 text-center">
                  <MagnifyingGlass size={40} className="text-slate-300 mx-auto mb-2" />
                  <p className="text-slate-500">Search for a patient to view encounters</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Encounter List & Detail */}
        <div className="lg:col-span-2">
          {selectedPatient ? (
            <div className="space-y-4">
              {/* Patient Header */}
              <div className="bg-teal-50 rounded-xl p-4 border border-teal-200">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-teal-100 rounded-full flex items-center justify-center">
                    <User size={28} className="text-teal-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-teal-900">
                      {selectedPatient.last_name}, {selectedPatient.first_name}
                    </h3>
                    <p className="text-sm text-teal-700">
                      DOB: {selectedPatient.date_of_birth || 'N/A'} • {selectedPatient.email}
                    </p>
                    <p className="text-sm text-teal-600">
                      {encounters.length} encounter(s) found
                    </p>
                  </div>
                </div>
              </div>

              {/* Encounter List */}
              {loadingEncounters ? (
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8 text-center">
                  <div className="spinner mx-auto mb-2" />
                  <p className="text-slate-500">Loading encounters...</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {encounters.map((enc) => (
                    <div 
                      key={enc.id} 
                      className={`bg-white rounded-xl shadow-sm border border-slate-200 p-5 cursor-pointer hover:border-teal-300 transition-colors ${
                        selectedEncounter?.id === enc.id ? 'border-teal-500 ring-2 ring-teal-100' : ''
                      }`}
                      onClick={() => setSelectedEncounter(enc)}
                      data-testid={`encounter-${enc.id}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                            enc.encounter_type === 'telehealth' ? 'bg-purple-100' : 'bg-blue-100'
                          }`}>
                            {enc.encounter_type === 'telehealth' ? (
                              <VideoCamera size={24} className="text-purple-600" />
                            ) : (
                              <Stethoscope size={24} className="text-blue-600" />
                            )}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h4 className="font-semibold text-slate-900">
                                {enc.encounter_type === 'telehealth' ? 'Telehealth Visit' : 'Office Visit'}
                              </h4>
                              {getStatusBadge(enc.status)}
                            </div>
                            <p className="text-sm text-slate-500 flex items-center gap-1">
                              <Calendar size={14} />
                              {formatDateTime(enc.created_at)}
                            </p>
                            {enc.provider_name && (
                              <p className="text-sm text-slate-500">Provider: {enc.provider_name}</p>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          {enc.addendums?.length > 0 && (
                            <span className="text-xs font-medium text-amber-600 bg-amber-50 px-2 py-1 rounded-full flex items-center gap-1">
                              <NotePencil size={12} />
                              {enc.addendums.length} addendum{enc.addendums.length > 1 ? 's' : ''}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Show details if selected */}
                      {selectedEncounter?.id === enc.id && (
                        <div className="mt-4 pt-4 border-t border-slate-200 space-y-4">
                          {enc.diagnosis && (
                            <div>
                              <p className="text-xs font-medium text-slate-500 uppercase mb-1">Diagnosis</p>
                              <p className="text-sm text-slate-700">{enc.diagnosis}</p>
                            </div>
                          )}
                          {enc.treatment_plan && (
                            <div>
                              <p className="text-xs font-medium text-slate-500 uppercase mb-1">Treatment Plan</p>
                              <p className="text-sm text-slate-700">{enc.treatment_plan}</p>
                            </div>
                          )}
                          {enc.ai_scribe_notes && (
                            <div>
                              <p className="text-xs font-medium text-slate-500 uppercase mb-1">Clinical Notes</p>
                              <p className="text-sm text-slate-700 whitespace-pre-wrap">{enc.ai_scribe_notes}</p>
                            </div>
                          )}

                          {/* Addendums Section */}
                          {enc.addendums?.length > 0 && (
                            <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                              <h5 className="font-semibold text-amber-800 mb-3 flex items-center gap-2">
                                <NotePencil size={16} />
                                Addendums
                              </h5>
                              <div className="space-y-3">
                                {enc.addendums.map((add, idx) => (
                                  <div key={add.id || idx} className="bg-white rounded-lg p-3 border border-amber-200">
                                    {add.reason && (
                                      <p className="text-xs font-medium text-amber-600 mb-1">Reason: {add.reason}</p>
                                    )}
                                    <p className="text-sm text-slate-700">{add.text}</p>
                                    <p className="text-xs text-slate-500 mt-2">
                                      — {add.added_by_name} • {formatDateTime(add.added_at)}
                                    </p>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Add Addendum Button */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setAddendumModalOpen(true);
                            }}
                            className="btn-outline flex items-center gap-2 text-sm"
                            data-testid={`add-addendum-btn-${enc.id}`}
                          >
                            <Plus size={16} />
                            Add Addendum
                          </button>
                        </div>
                      )}
                    </div>
                  ))}

                  {encounters.length === 0 && (
                    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8 text-center">
                      <ClipboardText size={40} className="text-slate-300 mx-auto mb-2" />
                      <p className="text-slate-500">No encounters found for this patient</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-12 text-center">
              <Stethoscope size={48} className="text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-700 mb-2">Select a Patient</h3>
              <p className="text-slate-500">
                Search for a patient and select them to view their encounter history
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Add Addendum Modal */}
      {addendumModalOpen && selectedEncounter && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">Add Addendum</h3>
                <p className="text-sm text-slate-500 mt-1">
                  {selectedEncounter.encounter_type === 'telehealth' ? 'Telehealth' : 'Office'} Visit 
                  • {formatDate(selectedEncounter.created_at)}
                </p>
              </div>
              <button
                onClick={() => {
                  setAddendumModalOpen(false);
                  setAddendumText('');
                  setAddendumReason('');
                }}
                className="p-2 hover:bg-slate-100 rounded-lg"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-sm text-amber-800">
                  <Warning size={16} className="inline mr-1" weight="fill" />
                  Addendums are appended to the original encounter record. The original documentation remains unchanged for HIPAA compliance.
                </p>
              </div>
              
              <div>
                <label className="label-clinical">Reason for Addendum (Optional)</label>
                <select
                  value={addendumReason}
                  onChange={(e) => setAddendumReason(e.target.value)}
                  className="input-clinical"
                  data-testid="addendum-reason-select"
                >
                  <option value="">Select reason...</option>
                  <option value="Additional Information">Additional Information</option>
                  <option value="Clarification">Clarification</option>
                  <option value="Correction">Correction</option>
                  <option value="Late Entry">Late Entry</option>
                  <option value="Lab Results Follow-up">Lab Results Follow-up</option>
                  <option value="Patient Communication">Patient Communication</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              
              <div>
                <label className="label-clinical">Addendum Text *</label>
                <textarea
                  value={addendumText}
                  onChange={(e) => setAddendumText(e.target.value)}
                  placeholder="Enter your addendum here..."
                  className="input-clinical min-h-[150px] resize-none"
                  data-testid="addendum-text-input"
                  required
                />
              </div>
              
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => {
                    setAddendumModalOpen(false);
                    setAddendumText('');
                    setAddendumReason('');
                  }}
                  className="btn-outline flex-1"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddAddendum}
                  disabled={!addendumText.trim() || savingAddendum}
                  className="btn-teal flex-1 flex items-center justify-center gap-2"
                  data-testid="save-addendum-btn"
                >
                  {savingAddendum ? (
                    <>
                      <div className="spinner-sm" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <CheckCircle size={18} />
                      Save Addendum
                    </>
                  )}
                </button>
              </div>
              
              <p className="text-xs text-slate-500 text-center">
                This addendum will be timestamped and signed with your name.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
